import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Crown, Building2, Banknote, Landmark, Pickaxe, Leaf, Wheat, MapPin,
  Cpu, Users, Sparkles, ChevronRight, Radar, BookOpen,
} from "lucide-react";

const GOLD = "#C99A2E";
const PURPLE = "#7B2FBE";
const GREEN = "#6BB88A";
const BLUE = "#4FA3D1";
const TEAL = "#2E7D6B";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const auth = () => {
  const t = localStorage.getItem("access_token") || localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const PORTFOLIO_ICON = {
  president: Crown, cabinet: Building2, finance: Banknote, lands: Landmark,
  mines: Pickaxe, environment: Leaf, agriculture: Wheat, local_government: MapPin,
  ict: Cpu, provincial: Users,
};

const Card = ({ title, accent, right, children, testId }) => (
  <div className="rounded-sm border" style={{ borderColor: accent + "40", background: "rgba(11,53,88,0.35)" }} data-testid={testId}>
    <div className="px-3 py-2 border-b flex items-center gap-2" style={{ borderColor: accent + "25" }}>
      <div className="w-1 h-4 rounded-sm" style={{ background: accent }} />
      <div className="font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: accent }}>{title}</div>
      <div className="ml-auto">{right}</div>
    </div>
    <div className="p-3">{children}</div>
  </div>
);

const Chip = ({ children, color = BLUE, filled }) => (
  <span className="inline-flex items-center rounded-sm px-1.5 py-0.5 font-mono uppercase text-[9.5px] tracking-[0.14em]"
        style={{ background: filled ? color : color + "20", color: filled ? "#071B2B" : color, border: "1px solid " + color + "45" }}>
    {children}
  </span>
);

export const CabinetWorkspaces = () => {
  const [portfolios, setPortfolios] = useState([]);
  const [selected, setSelected] = useState("president");
  const [data, setData] = useState(null);
  const [ministries, setMinistries] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [p, m] = await Promise.all([
          axios.get(`${API}/cadastre360/phase-xv/portfolios`, { headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xv/ministry-profiles`, { headers: auth() }),
        ]);
        setPortfolios(p.data.portfolios); setMinistries(m.data.profiles);
      } catch (e) { toast.error("Failed to load portfolios — " + e.message); }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!selected) return;
      try {
        const r = await axios.get(`${API}/cadastre360/phase-xv/portfolios/${selected}`, { headers: auth() });
        setData(r.data);
      } catch (e) { toast.error("Portfolio load failed — " + e.message); }
    })();
  }, [selected]);

  const questions = [
    ["what_is_happening",   "What is happening?"],
    ["why",                  "Why?"],
    ["national_impact",      "National impact?"],
    ["financial_impact",     "Financial impact?"],
    ["legal_impact",         "Legal impact?"],
    ["environmental_impact", "Environmental impact?"],
    ["investment_impact",    "Investment impact?"],
    ["citizen_impact",       "Citizen impact?"],
    ["recommended_action",   "Recommended action?"],
    ["ai_recommendation",    "AI recommendation?"],
  ];

  return (
    <div className="p-6 space-y-4" data-testid="page-cabinet-workspaces">
      <div className="rounded-sm border p-6 flex items-start gap-4"
           style={{ borderColor: PURPLE + "60",
                     background: `linear-gradient(120deg, rgba(7,27,43,0.95) 0%, rgba(11,53,88,0.85) 45%, ${PURPLE}30 100%)` }}>
        <Building2 size={38} className="mt-1" style={{ color: PURPLE }} />
        <div className="flex-1">
          <div className="font-mono uppercase tracking-[0.32em] text-[10px]" style={{ color: PURPLE }}>
            Cadastre360™ · Phase XV · Cabinet Portfolio Intelligence™
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-[#F6F8FA] mt-1">
            Cabinet Portfolio Workspaces
          </h1>
          <div className="text-[13px] text-[#DCE3E8] mt-2 max-w-4xl leading-relaxed">
            Executive decision centres for the President, Cabinet, Treasury and every portfolio minister —
            consuming existing enterprise services. Not operational portals; decision support.
          </div>
        </div>
      </div>

      {/* Portfolio selector */}
      <Card title="Select Cabinet Portfolio" accent={GOLD}>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {portfolios.map((p) => {
            const Icon = PORTFOLIO_ICON[p.key] || Building2;
            const active = selected === p.key;
            return (
              <button key={p.key} onClick={() => setSelected(p.key)}
                      className="rounded-sm border px-3 py-2 flex items-center gap-2 text-left"
                      style={{ borderColor: (active ? PURPLE : GOLD) + "60",
                                background: active ? PURPLE + "25" : "rgba(7,27,43,0.55)" }}
                      data-testid={`portfolio-${p.key}`}>
                <Icon size={14} style={{ color: active ? PURPLE : GOLD }} />
                <div className="flex-1 text-[11.5px] text-[#F6F8FA]">{p.label}</div>
                <ChevronRight size={11} className="text-[#7C93A5]" />
              </button>
            );
          })}
        </div>
      </Card>

      {/* Portfolio KPI deck + Decision Support */}
      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <Card title={data.portfolio.label} accent={PURPLE} testId="workspace-deck">
            <div className="grid grid-cols-2 gap-2">
              {data.kpis.map((k) => (
                <div key={k.label} className="rounded-sm border px-2 py-1.5" style={{ borderColor: PURPLE + "30", background: "rgba(7,27,43,0.55)" }}>
                  <div className="font-mono uppercase text-[9.5px] text-[#7C93A5] tracking-[0.16em]">{k.label}</div>
                  <div className="text-[13px] font-bold text-[#F6F8FA]">{k.value}</div>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Portfolio Decision Support · every workspace answers" accent={BLUE} testId="workspace-decision-support" >
            <div className="grid grid-cols-1 gap-1.5">
              {questions.map(([k, q]) => (
                <div key={k} className="rounded-sm border p-1.5" style={{ borderColor: BLUE + "30", background: "rgba(7,27,43,0.5)" }}>
                  <div className="font-mono uppercase text-[9.5px] text-[#7C93A5]">{q}</div>
                  <div className="text-[11.5px] text-[#F6F8FA]">{data.decision_support[k]}</div>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Live Snapshot" accent={GREEN}>
            <div className="grid grid-cols-1 gap-1">
              {Object.entries(data.snapshot || {}).map(([k, v]) => (
                <div key={k} className="rounded-sm border px-2 py-1 flex items-center gap-2" style={{ borderColor: GREEN + "30", background: "rgba(7,27,43,0.5)" }}>
                  <div className="flex-1 font-mono uppercase text-[9.5px] text-[#7C93A5]">{k}</div>
                  <div className="text-[11.5px] text-[#F6F8FA]">{typeof v === "number" ? v.toLocaleString() : String(v)}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Ministry Operating Profiles */}
      <Card title="Ministry Operating Profiles™" accent={GOLD} testId="ministry-profiles"
            right={<Chip color={GOLD}>{ministries.length} ministries</Chip>}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {ministries.map((m) => (
            <div key={m.key} className="rounded-sm border p-2" style={{ borderColor: GOLD + "35", background: "rgba(7,27,43,0.55)" }}
                 data-testid={`ministry-${m.key}`}>
              <div className="text-[12.5px] font-semibold text-[#F6F8FA] mb-1">{m.label}</div>
              <div className="grid grid-cols-2 gap-1 mb-1">
                {m.kpis.map((k) => (
                  <div key={k.label} className="text-[10.5px] text-[#B3C2CC]">
                    {k.label}: <span className="text-[#F6F8FA] font-mono">{k.value}</span>
                  </div>
                ))}
              </div>
              <div className="text-[10.5px] text-[#7C93A5]">Budget: {m.budget_utilisation_pct}% · Projects: {m.projects} · Decisions: {m.cabinet_decisions_open}</div>
              <div className="text-[10.5px] text-[#B3C2CC] italic mt-1"><Sparkles size={10} className="inline mr-1" style={{ color: PURPLE }} />{m.ai_recommendation}</div>
            </div>
          ))}
        </div>
      </Card>

      <div className="text-[10.5px] text-[#7C93A5] font-mono uppercase tracking-[0.18em] pt-1">
        Cadastre360™ · Phase XV · Cabinet portfolio intelligence · additive · zero regression
      </div>
    </div>
  );
};

export default CabinetWorkspaces;
